/**
 * Le contrat de la supervision des comptes joints, côté navigateur (lot 6 de
 * `docs/PLAN_COMPTE_JOINT.md`).
 *
 * **Ce fichier ne décide de rien.** Les types suivent
 * `JointAccountSupervisionMessages.java`, champ pour champ, et un contrôle les y
 * confronte (`test/app/joint-accounts.test.tsx`).
 *
 * ## Deux lectures, quatre écritures — et pas ici
 *
 * La liste et la fiche sont servies par `JointAccountsApi`, sous
 * `JOINT_ACCOUNT_VIEW`, avec un motif inscrit au journal. Les quatre gestes —
 * geler, dégeler, enregistrer un décès, relancer les parts — partent sur la
 * route unique des actions. Le gel et la relance s'exécutent à une signature ;
 * le dégel et le décès sont en double validation : le socle rend `202` avec
 * l'identifiant d'une demande, et **rien n'a changé**. L'écran doit le dire
 * ainsi.
 *
 * ## Les montants sont en unités mineures, et un nul n'est pas zéro
 *
 * Un sous-compte introuvable au grand livre n'a pas de solde : ce n'est pas
 * « 0 F », c'est une anomalie.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin de la supervision. */
export const jointAccountsPath = '/api/backoffice/joint-accounts';

/** Le chemin de la route unique des actions habilitables. */
export const jointAccountActionsPath = '/api/backoffice/actions';

/** Les quatre gestes, par leur code d'action (V106). */
export const jointAccountActions = {
  freeze: 'JOINT_ACCOUNT_FREEZE',
  unfreeze: 'JOINT_ACCOUNT_UNFREEZE',
  deceased: 'JOINT_HOLDER_DECEASED',
  retryShares: 'JOINT_SHARE_RETRY',
} as const;

/** Le code d'un des quatre gestes. */
export type JointAccountGesture = keyof typeof jointAccountActions;

/** Le motif écrit au journal pour les lectures de la supervision. */
export const jointAccountsReason = 'supervision-des-comptes-joints';

/** Le filtre qui ne garde que les comptes dont une part est en souffrance. */
export const stuckSharesFilter = 'PARTS_EN_SOUFFRANCE';

/** Le filtre qui ne garde que les comptes dont un titulaire est décédé. */
export const deceasedFilter = 'DECES';

/** Une ligne de la liste. Suit `JointSupervisionRowView`. */
export interface JointAccountRow {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly initiatorName: string | null;
  readonly initiatorMsisdn: string | null;
  readonly ruleKind: string;
  readonly activeHolders: number;
  readonly ledgerBalance: number | null;
  readonly pendingRequests: number;
  readonly unpaidFees: number;
  readonly pendingShares: number;
  readonly stuckShares: number;
  readonly hasDeceasedHolder: boolean;
  readonly createdAt: string | null;
  readonly activatedAt: string | null;
  readonly frozenAt: string | null;
  readonly closedAt: string | null;
}

/** Une page de la liste. Suit `JointSupervisionPageView`. */
export interface JointAccountPage {
  readonly jointAccounts: readonly JointAccountRow[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly frozenCount: number;
  readonly deceasedCount: number;
  readonly stuckShareCount: number;
}

/** La règle de signature. Suit `JointSupervisionRuleView`. */
export interface JointAccountRule {
  readonly kind: string;
  readonly threshold: number | null;
  readonly quorum: number | null;
}

/** Un titulaire, identité complète. Suit `JointSupervisionHolderView`. */
export interface JointAccountHolder {
  readonly holderId: string;
  readonly customerId: string;
  readonly publicRef: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly msisdn: string | null;
  readonly displayName: string;
  readonly role: string;
  readonly status: string;
  readonly contributedTotal: number;
  readonly receivedTotal: number;
  readonly net: number;
  readonly estimatedShare: number | null;
  readonly invitedAt: string | null;
  readonly acceptedAt: string | null;
  readonly leftAt: string | null;
}

/** Une demande, close ou non. Suit `JointSupervisionRequestView`. */
export interface JointAccountRequest {
  readonly id: string;
  readonly kind: string;
  readonly status: string;
  readonly requesterHolderId: string;
  readonly requesterName: string | null;
  readonly amount: number | null;
  readonly newRuleKind: string | null;
  readonly newThreshold: number | null;
  readonly newQuorum: number | null;
  readonly requiredSignatures: number;
  readonly signed: number;
  readonly refused: number;
  readonly createdAt: string | null;
  readonly expiresAt: string | null;
  readonly decidedAt: string | null;
  readonly transactionId: string | null;
  readonly failureReason: string | null;
}

/** Une échéance de frais de tenue. Suit `JointSupervisionFeeView`. */
export interface JointAccountFee {
  readonly id: string;
  readonly month: string;
  readonly dueOn: string;
  readonly status: string;
  readonly amount: number | null;
  readonly attempts: number;
  readonly lastAttemptAt: string | null;
  readonly chargedAt: string | null;
  readonly transactionId: string | null;
}

/** Une part de dissolution. Suit `JointSupervisionShareView`. */
export interface JointAccountShare {
  readonly id: string;
  readonly holderId: string;
  readonly holderName: string | null;
  readonly amount: number;
  readonly status: string;
  readonly attempts: number;
  readonly lastAttemptAt: string | null;
  readonly lastFailure: string | null;
  readonly paidAt: string | null;
  readonly transactionId: string | null;
  readonly reservedForSuccession: boolean;
  readonly stuck: boolean;
}

/** Une décision du back-office. Suit `JointSupervisionDecisionView`. */
export interface JointAccountDecision {
  readonly kind: string;
  readonly holderId: string | null;
  readonly holderName: string | null;
  readonly reason: string;
  readonly evidence: string | null;
  readonly approvalRequestId: string | null;
  readonly requestedBy: string;
  readonly approvedBy: string | null;
  readonly occurredAt: string | null;
}

/** La fiche d'un compte joint. Suit `JointSupervisionDetailView`. */
export interface JointAccountDetail {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly accountNumber: string | null;
  readonly ledgerBalance: number | null;
  readonly unpaidFeesDue: number;
  readonly rule: JointAccountRule;
  readonly createdAt: string | null;
  readonly activatedAt: string | null;
  readonly frozenAt: string | null;
  readonly closedAt: string | null;
  readonly holders: readonly JointAccountHolder[];
  readonly requests: readonly JointAccountRequest[];
  readonly fees: readonly JointAccountFee[];
  readonly shares: readonly JointAccountShare[];
  readonly decisions: readonly JointAccountDecision[];
  readonly freezable: boolean;
  readonly unfreezable: boolean;
  readonly sharesRetryable: boolean;
}

/** Ce que rend la route des actions. */
export interface JointAccountActionOutcome {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/**
 * Lit une page de comptes joints.
 *
 * @param filter un état, `PARTS_EN_SOUFFRANCE`, `DECES`, ou `null` pour tous
 * @param search un nom, un numéro, une référence — ou `null`
 * @param page   à partir de 1
 */
export async function fetchJointAccounts(
  client: ApiClient,
  filter: string | null,
  search: string | null,
  page: number,
  reason: string = jointAccountsReason,
): Promise<JointAccountPage> {
  const parametres = new URLSearchParams({ reason, page: String(page) });
  if (filter !== null) parametres.set('status', filter);
  if (search !== null && search.trim() !== '') parametres.set('q', search.trim());
  const recue = await client.request<JointAccountPage>(
    `${jointAccountsPath}?${parametres.toString()}`,
  );
  // **Une réponse de travers ne doit pas emporter l'écran.** Sans cette garde,
  // `.map` sur un objet qui n'est pas une liste lèverait jusqu'à la racine React.
  if (!Array.isArray(recue?.jointAccounts)) {
    throw new Error('ÉCHEC : la réponse qui devait porter les comptes joints n’est pas une page.');
  }
  return recue;
}

/** Lit la fiche d'un compte joint. */
export async function fetchJointAccount(
  client: ApiClient,
  id: string,
  reason: string = jointAccountsReason,
): Promise<JointAccountDetail> {
  const parametres = new URLSearchParams({ reason });
  const recue = await client.request<JointAccountDetail>(
    `${jointAccountsPath}/${encodeURIComponent(id)}?${parametres.toString()}`,
  );
  if (!Array.isArray(recue?.holders) || !Array.isArray(recue?.requests)) {
    throw new Error('ÉCHEC : la réponse qui devait porter un compte joint n’en est pas un.');
  }
  return recue;
}

/**
 * Demande un des quatre gestes sur un compte joint.
 *
 * Le gel et la relance s'exécutent ; le dégel et le décès rendent `202` et
 * l'identifiant d'une demande — **rien n'a changé** tant qu'une seconde
 * signature ne l'a pas approuvée.
 */
export async function requestJointAccountGesture(
  client: ApiClient,
  demande: {
    readonly gesture: JointAccountGesture;
    readonly jointAccountId: string;
    readonly reason: string;
    readonly payload?: Readonly<Record<string, string>>;
  },
): Promise<JointAccountActionOutcome> {
  return client.request<JointAccountActionOutcome>(
    `${jointAccountActionsPath}/${jointAccountActions[demande.gesture]}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityRef: demande.jointAccountId,
        reason: demande.reason,
        payload: demande.payload ?? {},
      }),
    },
  );
}
